import React, { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { toast } from "sonner";
import { fetchProducts, createProduct, updateProduct, deleteProduct } from "@/lib/api";
import { CATEGORY_LABELS } from "@/lib/util";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { Label } from "@/components/ui/label";
import { Switch } from "@/components/ui/switch";
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from "@/components/ui/select";
import { ProductImage } from "@/components/ProductImage";
import { ArrowLeft, Plus, Save, Trash2, Pencil, Archive, Eye } from "lucide-react";

const ZONES = [
  "candy-jars", "chocolate-counter", "biscuit-shelf", "chips-wall", "powdered-drinks",
  "cold-drinks-fridge", "icecream-freezer", "churan-counter",
  "gola-cart", "panipuri-stall", "juice-cart", "snack-cart",
];
const CATS = Object.keys(CATEGORY_LABELS);

const EMPTY = {
  name: "", category: "Candy", zone: "candy-jars", subcategory: "", brand: "",
  era_start: 1995, era_end: 2005, approximate_price: "", price_pack_size: "",
  tagline: "", nostalgia_description: "", historical_description: "",
  nostalgia_score: 3, image_url: "", images: [], image_source: "", image_license: "",
  image_attribution: "", external_source_url: "", region: "India", notes: "",
  placeholder_color: "#C84B31", discontinued: false, jingle_available: false,
  verified: false, published: true, archived: false,
};

// ---- module-scope helpers (stable identity → no focus loss) ----
const Field = ({ label, k, form, set, type = "text", placeholder }) => (
  <div className="space-y-1">
    <Label className="text-xs font-semibold text-kirana-brown">{label}</Label>
    <Input
      data-testid={`admin-field-${k}`}
      type={type}
      value={form[k]}
      placeholder={placeholder}
      onChange={(e) => set(k, e.target.value)}
      className="border-2 border-kirana-border bg-kirana-cream"
    />
  </div>
);

const ToggleRow = ({ label, k, form, set }) => (
  <div className="flex items-center justify-between rounded-md border-2 border-kirana-border bg-kirana-cream px-3 py-2">
    <Label className="text-xs font-semibold text-kirana-brown">{label}</Label>
    <Switch data-testid={`admin-toggle-${k}`} checked={!!form[k]} onCheckedChange={(v) => set(k, v)} />
  </div>
);

const Tag = ({ children, tone }) => (
  <span
    className={`rounded border px-1.5 py-0.5 font-bold ${
      tone === "green"
        ? "border-kirana-green bg-kirana-green/10 text-kirana-green"
        : tone === "brown"
        ? "border-kirana-brown bg-kirana-brown/10 text-kirana-brown"
        : "border-kirana-red bg-kirana-red/10 text-kirana-red"
    }`}
  >
    {children}
  </span>
);

export default function Admin() {
  const [items, setItems] = useState([]);
  const [form, setForm] = useState(EMPTY);
  const [editId, setEditId] = useState(null);
  const [saving, setSaving] = useState(false);

  const load = () =>
    fetchProducts(true).then(setItems).catch(() => toast.error("Failed to load products"));

  useEffect(() => {
    load();
  }, []);

  const set = (k, v) => setForm((f) => ({ ...f, [k]: v }));

  const startEdit = (p) => {
    setEditId(p.id);
    setForm({ ...EMPTY, ...p, images: p.images || [] });
    window.scrollTo({ top: 0, behavior: "smooth" });
  };

  const reset = () => {
    setEditId(null);
    setForm(EMPTY);
  };

  const save = async (overrides = {}) => {
    if (!form.name.trim()) {
      toast.error("Name is required");
      return;
    }
    setSaving(true);
    const payload = {
      ...form,
      ...overrides,
      era_start: Number(form.era_start),
      era_end: Number(form.era_end),
      nostalgia_score: Number(form.nostalgia_score),
    };
    try {
      if (editId) {
        await updateProduct(editId, payload);
        toast.success("Saved changes");
      } else {
        await createProduct(payload);
        toast.success("Product created");
      }
      reset();
      load();
    } catch {
      toast.error("Save failed");
    } finally {
      setSaving(false);
    }
  };

  const remove = async (id) => {
    if (!window.confirm("Delete this product permanently?")) return;
    await deleteProduct(id);
    toast.success("Deleted");
    if (editId === id) reset();
    load();
  };

  return (
    <div className="min-h-screen bg-background paper-grain">
      <header className="sticky top-0 z-40 border-b-[3px] border-kirana-brown bg-kirana-red px-4 py-3 text-kirana-cream">
        <div className="mx-auto flex max-w-6xl items-center justify-between">
          <h1 className="font-signboard text-2xl font-bold">Kirana Admin — Item Manager</h1>
          <Link
            to="/"
            data-testid="admin-back"
            className="flex items-center gap-1.5 rounded-md border-2 border-kirana-cream px-3 py-1.5 text-sm font-bold"
          >
            <ArrowLeft className="h-4 w-4" /> Back to Store
          </Link>
        </div>
      </header>

      <main className="mx-auto grid max-w-6xl gap-6 px-4 py-6 lg:grid-cols-[1fr_1.2fr]">
        {/* form */}
        <section className="rounded-lg border-[3px] border-kirana-brown bg-card p-5 hard-shadow">
          <div className="mb-4 flex items-center justify-between">
            <h2 className="font-signboard text-xl font-bold text-kirana-brown">
              {editId ? "Edit Item" : "Add New Item"}
            </h2>
            {editId && (
              <Button variant="outline" size="sm" onClick={reset} data-testid="admin-new">
                <Plus className="mr-1 h-4 w-4" /> New
              </Button>
            )}
          </div>

          {/* preview */}
          <div className="mb-4 flex items-center gap-3 rounded-md border-2 border-dashed border-kirana-border p-3">
            <div className="h-16 w-16 overflow-hidden rounded border-2 border-kirana-brown">
              <ProductImage product={form} />
            </div>
            <div className="text-sm">
              <p className="font-signboard text-lg font-bold text-kirana-brown">{form.name || "Preview"}</p>
              <p className="italic text-kirana-red">{form.tagline || form.nostalgia_description}</p>
              {form.images?.length > 0 && (
                <p className="text-xs text-kirana-brown/60">{form.images.length} image(s) in gallery</p>
              )}
            </div>
          </div>

          <div className="grid grid-cols-2 gap-3">
            <div className="col-span-2"><Field label="Name *" k="name" form={form} set={set} placeholder="e.g. Gold Spot" /></div>
            <div className="space-y-1">
              <Label className="text-xs font-semibold text-kirana-brown">Category</Label>
              <Select value={form.category} onValueChange={(v) => set("category", v)}>
                <SelectTrigger data-testid="admin-field-category" className="border-2 border-kirana-border bg-kirana-cream">
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  {CATS.map((c) => <SelectItem key={c} value={c}>{CATEGORY_LABELS[c]}</SelectItem>)}
                </SelectContent>
              </Select>
            </div>
            <div className="space-y-1">
              <Label className="text-xs font-semibold text-kirana-brown">Zone / Shelf</Label>
              <Select value={form.zone} onValueChange={(v) => set("zone", v)}>
                <SelectTrigger data-testid="admin-field-zone" className="border-2 border-kirana-border bg-kirana-cream">
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  {ZONES.map((z) => <SelectItem key={z} value={z}>{z}</SelectItem>)}
                </SelectContent>
              </Select>
            </div>
            <Field label="Brand" k="brand" form={form} set={set} />
            <Field label="Subcategory" k="subcategory" form={form} set={set} />
            <Field label="Era Start" k="era_start" type="number" form={form} set={set} />
            <Field label="Era End" k="era_end" type="number" form={form} set={set} />
            <Field label="Approx Price" k="approximate_price" form={form} set={set} placeholder="~₹5" />
            <Field label="Pack Size" k="price_pack_size" form={form} set={set} placeholder="22g" />
            <Field label="Nostalgia Score (1-5)" k="nostalgia_score" type="number" form={form} set={set} />
            <Field label="Placeholder Color" k="placeholder_color" form={form} set={set} />
            <div className="col-span-2"><Field label="Tagline / Jingle line" k="tagline" form={form} set={set} placeholder="Melody itni chocolaty kyun hai?" /></div>
            <div className="col-span-2 space-y-1">
              <Label className="text-xs font-semibold text-kirana-brown">Nostalgia / Memory Description</Label>
              <Textarea data-testid="admin-field-nostalgia_description" value={form.nostalgia_description}
                onChange={(e) => set("nostalgia_description", e.target.value)}
                className="border-2 border-kirana-border bg-kirana-cream" />
            </div>
            <div className="col-span-2 space-y-1">
              <Label className="text-xs font-semibold text-kirana-brown">Historical Description</Label>
              <Textarea data-testid="admin-field-historical_description" value={form.historical_description}
                onChange={(e) => set("historical_description", e.target.value)}
                className="border-2 border-kirana-border bg-kirana-cream" />
            </div>
            <div className="col-span-2"><Field label="Primary Image URL (blank = placeholder)" k="image_url" form={form} set={set} /></div>
            <div className="col-span-2 space-y-1">
              <Label className="text-xs font-semibold text-kirana-brown">Gallery Image URLs (one per line)</Label>
              <Textarea data-testid="admin-field-images" value={(form.images || []).join("\n")}
                placeholder={"https://.../cheetos_1.jpg\nhttps://.../cheetos_2.jpg"}
                onChange={(e) => set("images", e.target.value.split("\n").map((s) => s.trim()).filter(Boolean))}
                className="border-2 border-kirana-border bg-kirana-cream" />
            </div>
            <Field label="Image Source" k="image_source" form={form} set={set} />
            <Field label="Image License" k="image_license" form={form} set={set} />
            <Field label="Image Attribution" k="image_attribution" form={form} set={set} />
            <Field label="External Source URL" k="external_source_url" form={form} set={set} />
            <Field label="Region" k="region" form={form} set={set} />
            <div className="col-span-2"><Field label="Notes" k="notes" form={form} set={set} /></div>
          </div>

          <div className="mt-4 grid grid-cols-2 gap-3 sm:grid-cols-3">
            <ToggleRow label="Discontinued" k="discontinued" form={form} set={set} />
            <ToggleRow label="Jingle Available" k="jingle_available" form={form} set={set} />
            <ToggleRow label="Verified" k="verified" form={form} set={set} />
            <ToggleRow label="Published" k="published" form={form} set={set} />
            <ToggleRow label="Archived" k="archived" form={form} set={set} />
          </div>

          <div className="mt-5 flex flex-wrap gap-2">
            <Button data-testid="admin-save" onClick={() => save()} disabled={saving}
              className="bg-kirana-green text-white hover:bg-kirana-green/90">
              <Save className="mr-1 h-4 w-4" /> {editId ? "Save" : "Create"}
            </Button>
            <Button data-testid="admin-publish" variant="outline" onClick={() => save({ published: true, archived: false })} disabled={saving}>
              <Eye className="mr-1 h-4 w-4" /> Publish
            </Button>
            {editId && (
              <Button data-testid="admin-archive" variant="outline" onClick={() => save({ archived: true, published: false })} disabled={saving}>
                <Archive className="mr-1 h-4 w-4" /> Archive
              </Button>
            )}
          </div>
        </section>

        {/* list */}
        <section className="rounded-lg border-[3px] border-kirana-brown bg-card p-5 hard-shadow">
          <h2 className="mb-3 font-signboard text-xl font-bold text-kirana-brown">
            All Items ({items.length})
          </h2>
          <div className="max-h-[75vh] space-y-2 overflow-y-auto scrollbar-thin pr-1">
            {items.map((p) => (
              <div
                key={p.id}
                data-testid={`admin-row-${p.id}`}
                className="flex items-center gap-3 rounded-md border-2 border-kirana-border bg-kirana-cream p-2"
              >
                <div className="h-10 w-10 shrink-0 overflow-hidden rounded border border-kirana-brown">
                  <ProductImage product={p} />
                </div>
                <div className="min-w-0 flex-1">
                  <p className="truncate text-sm font-bold text-kirana-brown">
                    {p.name}{" "}
                    <span className="text-xs font-normal text-kirana-brown/60">
                      · {p.category} · {p.era_start}
                    </span>
                  </p>
                  <div className="flex flex-wrap gap-1 text-[10px]">
                    {p.discontinued && <Tag>Discontinued</Tag>}
                    {p.archived && <Tag tone="brown">Archived</Tag>}
                    {!p.published && <Tag tone="brown">Draft</Tag>}
                    {p.verified ? <Tag tone="green">Verified</Tag> : <Tag>Unverified</Tag>}
                    <Tag>NS {p.nostalgia_score}</Tag>
                  </div>
                </div>
                <button data-testid={`admin-edit-${p.id}`} onClick={() => startEdit(p)}
                  className="rounded border-2 border-kirana-brown bg-kirana-gold p-1.5 text-kirana-brown">
                  <Pencil className="h-3.5 w-3.5" />
                </button>
                <button data-testid={`admin-delete-${p.id}`} onClick={() => remove(p.id)}
                  className="rounded border-2 border-kirana-brown bg-kirana-red p-1.5 text-white">
                  <Trash2 className="h-3.5 w-3.5" />
                </button>
              </div>
            ))}
          </div>
        </section>
      </main>
    </div>
  );
}
